<template>
  <div class="aSelectField field">

    <!-- mask to close field if user click outside the field -->
    <div
        class="inputmask"
        v-if="isOptionsListOpen"
        @click="closeWithoutSelection"
    ></div>

    <div class="selectfield">
      <label :for="fieldname"><span v-html="descriptionReformated"></span><span v-if="isRequired" class="req" tabindex="-1">&nbsp;*</span></label>

      <div class="dropdown" :class="{'open': isOptionsListOpen}">

        <!-- ACCESSIBILITY : added aria-expanded-->
        <input
            type="text"
            autocomplete="off"
            :id="fieldname"
            v-model="textInField"
            @input="userChangedInput"
            :placeholder = "placeholder"
            :class="{'hasSuccess': isInSuccess, 'hasError': isInError, 'isLoading': isLoading}"
            @keyup.prevent.down="keyPressedDown"
            @keyup.prevent.up = "keyPressedUp"
            @keyup.prevent.enter = "keyPressedEnter"
            @keydown.tab="tabPressed"
            :disabled="isDisabled"
            :aria-expanded="isOptionsListOpen"
            aria-controls="dropdown-list-selectmeta"
            role="combobox"
            ref="textInput"
        >

        <!-- Toggler -->
        <div class="toggle" role="button" @click="toggleListOpeness()" @keyup.prevent.enter="toggleListOpeness()" :class="{'active': isOptionsListOpen}" aria-controls="dropdown-list-selectmeta" :aria-expanded="isOptionsListOpen" aria-label="dropdown list button">
					<span class="arrow-up" v-show="isOptionsListOpen">
						<svg height="20px" width="20px" version="1.1" class="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                 viewBox="0 0 36 36" xml:space="preserve">
						<g>
							<polygon points="0,24 18,6 36,24 31,28 18,15 5,28"/>
						</g>
					</svg>
				</span>
          <span class="arrow-down" v-show="!isOptionsListOpen">
					<svg height="20px" width="20px" version="1.1" class="Capa_2" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
               viewBox="0 0 36 36" xml:space="preserve">
					<g>
						<polygon  points="5,8 18,21 31,8 36,12 18,30 0,12"/>
					</g>
				</svg>
			</span>
        </div>

        <!-- Options list -->
        <ul
            v-show="isOptionsListOpen"
            id="dropdown-list-selectmeta"
            class="options-list"
            tabindex="-1"
            role="listbox"
            aria-label="dropdown field"
            @keydown.tab="tabPressed"
            @keyup.prevent.enter="keyPressedEnter"
            @keyup.prevent.esc="closeWithoutSelection"
            @keyup.prevent.left = "keyPressedLeftOrRight"
            @keyup.prevent.right = "keyPressedLeftOrRight"
        >
          <li
              v-for="(option, index) in optionsList"
              :key="index"
              @click="selectThisOption(option, index)"
              :class="{'active': currentActiveOption === index}"
              tabindex="0"
              role="option"
              :aria-label="option.description"
          >{{option.description}}</li>
          <li v-show="noResult" @click="closeWithoutSelection">{{$t('inputLabel.noResult')}}</li>
        </ul>

      </div>
    </div>

    <!-- ACCESSIBILITY : added tabindex-->
    <div class="errorMessage" v-if="isInError" tabindex="0">{{errorMessage}}</div>
    <div class="helper" :class="fieldname + '-helper'" v-html="helper" role="note" :aria-label="helper"></div>

  </div>
</template>

<!-- ================================================================================== -->

<script>
import FormFields from "../mixins/FormFields";
import customLog from "../mixins/CustomLog";
import config from "../config";

export default {
  name:"SelectMeta",
  mixins: [
    customLog,
    FormFields,
  ],
  props: {
    dataSource: {
      type: String,
      required: true,
    },
    caractersBeforeOpenList : {
      type: Number,
      required: false,
      default: 3
    },
    autocomplete: {
      type: Boolean,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data(){
    return {
      metaData: [{
        leadmanagementmetadata: {
          description: "",
          leadmanagementmetadatavalue: []
        }
      }],
      optionsList: [],
      isOptionsListOpen: false,
      optionsFullList: [],
      displayedInTheField: null,
      config: config,
      currentActiveOption: null,
      noResult: false,
    }
  },
  computed: {

    descriptionReformated(){
      /* In case description is not there yet */
      let desc = "";
      /* If there is a description */
      if (this.metaData.description) {
        desc = this.metaData.description;
      }
      /* Only in french */
      if ( this.$i18n.locale.slice(0,2) === 'fr' ){
        /* remove possible space before "?" and then introduce a unbreakable space in front of any "?" */
        desc = desc.replace(/ ([?!:;])/g, "&nbsp;$1");
      }
      return desc;
    },

    textInField: {
      get(){
        return this.displayedInTheField;
      },
      set(newVal){
        this.displayedInTheField = newVal;
      }
    },

    metaOptionsList(){
      let list = this.metaData.leadmanagementmetadatavalue;
      if(typeof list !== "undefined") {
        for (let item of list){
          item.description = item.description.trim();
        }
      } else {
        return [];
      }
      return list;
    },

  },
  methods: {

    keyPressedDown(){
      if(this.isOptionsListOpen && this.currentActiveOption < this.optionsList.length -1){
        this.downOneItem();
      }
      if(!this.isOptionsListOpen){
        this.makeNewOptionsList(false);
        this.isOptionsListOpen = true;
      }
    },

    tabPressed(e){

      this.tabKeyWasPressed = true;
      this.keyDownWasPressed = false;

      if(this.isOptionsListOpen && this.currentActiveOption < this.optionsList.length){

        if(e.shiftKey) {
          this.upOneItem();
        }
        else{
          this.downOneItem();
        }

      }else if(!this.isOptionsListOpen && !e.shiftKey){
        this.makeNewOptionsList(false);
        this.isOptionsListOpen = true;
        this.currentActiveOption = 0;
      }else if(this.isOptionsListOpen && e.shiftKey){
        this.currentActiveOption -= 2;
      }

    },

    keyPressedUp(){
      if(this.isOptionsListOpen && this.currentActiveOption > 0){
        this.upOneItem();
      }else{
        this.closeOptionsList();
        this.currentActiveOption = null;
      }
    },

    keyPressedEnter(){
      if(this.isOptionsListOpen){
        this.log("Enter when list is open", 'low');
        this.selectThisOption(this.optionsList[this.currentActiveOption], this.currentActiveOption);
      }
    },
    keyPressedLeftOrRight(event){
      this.$refs.textInput.focus();
      this.currentActiveOption = null;
      if (event.key === 'ArrowLeft') {
        console.log('Left key pressed');
        this.$refs.textInput.focus();
      } else if (event.key === 'ArrowRight') {
        console.log('Right key pressed');
        this.$refs.textInput.focus();
      }
    },

    upOneItem(){
      this.currentActiveOption -= 1;
    },

    downOneItem(){
      this.currentActiveOption += 1;
    },

    selectThisOption(option, index){
      this.log(`User choose item ${option.id} - ${option.description}`, 'low');
      this.displayedInTheField = option.description;
      this.currentActiveOption = index;
      this.closeOptionsList(option.id);
    },

    closeWithoutSelection(){
      this.log("Closing the optionList without selection", 'low');
      this.textInField = "";
      this.closeOptionsList();
      this.currentActiveOption = null; /* maybe could be inchanged instead? */
      this.runValidationProcess();
      this.$emit('update:modelValue', null);
    },

    toggleListOpeness(){
      this.isOptionsListOpen = !this.isOptionsListOpen;
      if(this.isOptionsListOpen){
        this.displayedInTheField = "";
        this.makeNewOptionsList(this.autocomplete);
      }
    },

    emptyAndCloseList(){
      this.optionsList = [];
      this.closeOptionsList();
    },

    userChangedInput(){
      /* Refresh list of options */
      /* User left no caracters => options list is empty */
      if(this.displayedInTheField.length === 0){
        this.emptyAndCloseList();
      }
      /* User type some caracters => options list matching strings */
      if(this.displayedInTheField.length >= this.caractersBeforeOpenList){
        this.makeNewOptionsList(this.autocomplete);
        if(!this.isOptionsListOpen){ this.openOptionsList(); }
      }
      /* Display "no result" instead of nothing */
      this.noResult = (this.displayedInTheField.length >= this.caractersBeforeOpenList && this.optionsList.length < 1);
    },

    makeNewOptionsList(bool){
      /* If autocomplete is true, it's a partial list */
      if(bool){
        let userInput = this.displayedInTheField.toLowerCase();
        this.optionsList = this.optionsFullList.filter(option => option.description.toLowerCase().includes(userInput));
      }
      /* If autocomplete is false, it's a full list */
      if(!bool){
        this.optionsList = this.optionsFullList;
      }
    },

    openOptionsList(){
      /* User open the option list */
      this.log("Opening option list", 'low');
      /* remove previous options */
      this.optionsList = [];
      /* recreate new options list */
      this.makeNewOptionsList(this.autocomplete);
      /* DOM open the list */
      this.isOptionsListOpen = true;
    },

    closeOptionsList(id){
      /* User leave the option list */
      this.noResult = false;
      this.log("Closing option list", 'low');
      this.isOptionsListOpen = false;
      this.runValidationProcess(id);
    },

    isThisFieldValid(){
      /* What is a valid answer ? An answer that is exactly in the list */
      let validOptionsList = [];
      for(let item in this.optionsFullList){
        validOptionsList.push(this.optionsFullList[item].description.trim().toLowerCase());
      }
      return validOptionsList.includes(this.displayedInTheField.trim().toLowerCase());
    },

    sendDataToForm(id){
      this.log(`SelectMeta transmitting id ${id} to parent`, 'low');
      this.$emit('update:modelValue', id);
    },

    sendValidityOfTheFieldToTheForm(bool){
      this.$emit('runCheck', {fieldname: this.fieldname, valid: bool});
    },

    whatToDoInCaseFieldIsValidated(id){
      this.log("Validation return true", 'success');
      this.sendDataToForm(id);
      this.giveSuccess();
      this.sendValidityOfTheFieldToTheForm(true);
    },

    whatToDoInCaseFieldIsNotValidated(){
      this.log("Validation return false", 'warning');
      this.giveError();
      this.errorMessage = this.$t('inputsTexts.dropdown.missingData');
      this.sendValidityOfTheFieldToTheForm(false);
    },

    runValidationProcess(id){
      this.removeSuccessOrError();

      /* If the field is empty, two possibilities */
      if(id === undefined){
        if(this.isRequired){
          this.whatToDoInCaseFieldIsNotValidated()
        }
        if(!this.isRequired){
          this.sendValidityOfTheFieldToTheForm(false);
        }
      }
      /* If the field is not empty */
      if(id && id.toString().length >= 0){

        this.isThisFieldValid() ? this.whatToDoInCaseFieldIsValidated(id) : this.whatToDoInCaseFieldIsNotValidated();
      }
    },

    apiForDropdownIsSuccessful(data){
      this.log("Data gathered from API", 'low');
      this.log(data);
      this.metaData = data;
      this.optionsFullList = this.metaOptionsList;
    },

    apiForDropdownHasFailed(error){
      this.log("apiForDropdownHasFailed", 'alert');
      this.log(error);
      if(error.response.status === 503){
        this.setNotLoading();
        this.$emit('maintenanceDetected');
      }else{
        this.log("Cannot get list from API");
        this.log(error);
      }
    },

    requestNewListFromAPI(){
      // this.optionsList = [{id:0, description: "Lorem ipsum"}];
      this.setLoading();
      this.axios.get(`/dropdown/${config.idlead}/metadata/${this.dataSource}`)
          .then(resp => {
            this.apiForDropdownIsSuccessful(resp.data);
          })
          .catch(error => {
            this.apiForDropdownHasFailed(error);
          })
          .then(()=>{
            this.log(`List from API are put in place in ${this.fieldname}.`, 'low');
          });
      this.setNotLoading();
    },

  },
  watch:{
    /* If locale change, must reload the list to take lang into account */
    '$store.getters.getLocale': function(){
      this.requestNewListFromAPI();
    },
  },
  mounted(){
    this.requestNewListFromAPI();
  }
}
</script>

<!-- ================================================================================== -->


<style lang="scss" scoped>
</style>

